import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Form,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaCalendarAlt } from 'react-icons/fa';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import Select from 'react-select';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import { MASK_DATE } from '~/constant';
import * as api from '~/Services/Service';
import { salvarEmExcel, formatarMonetario } from '~/Utils/Helpers';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import GraficoTempoMedioColaborador from './GraficoTempoMedioColaborador';

var ITENS_POR_PAGINA = 20;

var COLUNAS = [
  { campo: 'nome_atendente', titulo: 'Nome Atendente', align: 'left', width: '14%', truncate: true },
  { campo: 'data_inicio', titulo: 'Data Início', align: 'center', width: '8%' },
  { campo: 'data_fim', titulo: 'Data Fim', align: 'center', width: '8%' },
  { campo: 'nome_corretor', titulo: 'Nome Corretor', align: 'left', width: '14%', truncate: true },
  { campo: 'prospecto', titulo: 'Cód. Prospecto', align: 'center', width: '7%' },
  { campo: 'nome_cliente', titulo: 'Nome Cliente', align: 'left', width: '16%', truncate: true },
  { campo: 'proposta', titulo: 'Proposta', align: 'center', width: '7%' },
  { campo: 'pre_venda', titulo: 'Pré-Venda', align: 'center', width: '7%' },
  { campo: 'quadra', titulo: 'Quadra', align: 'center', width: '5%' },
  { campo: 'lote', titulo: 'Lote', align: 'center', width: '5%' },
  { campo: 'valor_vista', titulo: 'Valor à Vista', align: 'right', width: '8%' },
  { campo: 'tempo_atendimento', titulo: 'Tempo Atend.', align: 'center', width: '7%' },
];

var OPTIONS_TIPO_ATENDIMENTO = [
  { value: 'todos', label: 'Todos os Atendimentos' },
  { value: 'com_venda', label: 'Apenas com Vendas' },
];

var formatarData = function (dataStr) {
  if (!dataStr) return '';
  var dataMoment = moment(dataStr);
  if (!dataMoment.isValid()) return '';
  return dataMoment.format('DD/MM/YYYY');
};

var converterData = function (dataStr) {
  if (!dataStr || dataStr.trim() === '') return null;
  var dataMoment = moment(dataStr, 'DD/MM/YYYY', true);
  if (!dataMoment.isValid()) return null;
  return dataMoment.format('YYYY-MM-DD');
};

var calcularDuracaoMinutos = function (dataInicio, dataFim) {
  if (!dataInicio || !dataFim) return null;
  var inicio = moment(dataInicio);
  var fim = moment(dataFim);
  if (!inicio.isValid() || !fim.isValid()) return null;
  var diff = fim.diff(inicio, 'minutes');
  return diff >= 0 ? diff : null;
};

var formatarTempo = function (minutos) {
  if (minutos == null || isNaN(minutos)) return '-';
  if (minutos < 60) return Math.round(minutos) + ' min';
  var horas = Math.floor(minutos / 60);
  var mins = Math.round(minutos % 60);
  return horas + 'h ' + mins + 'min';
};

var renderizarCelula = function (item, col) {
  var valor = item[col.campo];
  if (col.campo === 'data_inicio' || col.campo === 'data_fim') {
    return formatarData(valor);
  }
  if (col.campo === 'valor_vista') {
    return valor ? formatarMonetario(valor, false) : '';
  }
  if (col.campo === 'tempo_atendimento') {
    return formatarTempo(calcularDuracaoMinutos(item.data_inicio, item.data_fim));
  }
  if (valor == null) return '';
  return valor;
};

var RelatorioAtendimentoLancamento = function () {
  var empreendimentos = useSelector(function (state) {
    return state.empreendimentos;
  });

  var [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(null);
  var [dados, setDados] = useState([]);
  var [isLoading, setIsLoading] = useState(false);
  var [isLoadingExcel, setIsLoadingExcel] = useState(false);
  var [paginaAtual, setPaginaAtual] = useState(1);
  var [ordenacao, setOrdenacao] = useState({ campo: '', direcao: 'asc' });

  var [dataInicial, setDataInicial] = useState('');
  var [dataFinal, setDataFinal] = useState('');
  var [prospecto, setProspecto] = useState('');
  var [preVenda, setPreVenda] = useState('');
  var [proposta, setProposta] = useState('');
  var [quadra, setQuadra] = useState('');
  var [lote, setLote] = useState('');
  var [filtroNomeCorretor, setFiltroNomeCorretor] = useState(null);
  var [filtroNomeAtendente, setFiltroNomeAtendente] = useState(null);
  var [filtroTipoAtendimento, setFiltroTipoAtendimento] = useState(OPTIONS_TIPO_ATENDIMENTO[0]);
  var [openDatePickerInicial, setOpenDatePickerInicial] = useState(false);
  var [openDatePickerFinal, setOpenDatePickerFinal] = useState(false);

  var isLoadingEmpreendimentos =
    !empreendimentos ||
    !empreendimentos.sort ||
    empreendimentos.sort.length === 0;

  var optionsEmpreendimento = useMemo(
    function () {
      if (isLoadingEmpreendimentos) return [];
      return empreendimentos.sort.map(function (item) {
        return {
          value: item.codigo,
          label: item.codigo + ' - ' + item.descricao,
        };
      });
    },
    [empreendimentos, isLoadingEmpreendimentos]
  );

  var optionsNomeCorretor = useMemo(
    function () {
      if (!dados || dados.length === 0) return [];
      var nomes = {};
      dados.forEach(function (item) {
        if (item.nome_corretor) {
          nomes[item.nome_corretor] = true;
        }
      });
      return Object.keys(nomes).sort().map(function (nome) {
        return { value: nome, label: nome };
      });
    },
    [dados]
  );

  var optionsNomeAtendente = useMemo(
    function () {
      if (!dados || dados.length === 0) return [];
      var nomes = {};
      dados.forEach(function (item) {
        if (item.nome_atendente) {
          nomes[item.nome_atendente] = true;
        }
      });
      return Object.keys(nomes).sort().map(function (nome) {
        return { value: nome, label: nome };
      });
    },
    [dados]
  );

  var dadosFiltrados = useMemo(
    function () {
      var resultado = dados;
      if (filtroTipoAtendimento && filtroTipoAtendimento.value === 'com_venda') {
        resultado = resultado.filter(function (item) {
          return item.pre_venda > 0;
        });
      }
      if (filtroNomeCorretor) {
        resultado = resultado.filter(function (item) {
          return item.nome_corretor === filtroNomeCorretor.value;
        });
      }
      if (filtroNomeAtendente) {
        resultado = resultado.filter(function (item) {
          return item.nome_atendente === filtroNomeAtendente.value;
        });
      }
      return resultado;
    },
    [dados, filtroNomeCorretor, filtroNomeAtendente, filtroTipoAtendimento]
  );

  var dadosPaginados = useMemo(
    function () {
      var inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
      var fim = inicio + ITENS_POR_PAGINA;
      return dadosFiltrados.slice(inicio, fim);
    },
    [dadosFiltrados, paginaAtual]
  );

  var totalPaginas = useMemo(
    function () {
      return Math.ceil(dadosFiltrados.length / ITENS_POR_PAGINA);
    },
    [dadosFiltrados.length]
  );

  // Cards de resumo (refletem o conjunto filtrado — mesmo escopo do Excel)
  var resumo = useMemo(
    function () {
      var totalAtendimentos = dadosFiltrados.length;
      var preVendasUnicas = new Set(dadosFiltrados.filter(function (d) { return d.pre_venda > 0; }).map(function (d) { return d.pre_venda; }));
      var totalConversoes = preVendasUnicas.size;
      var taxaConversao = totalAtendimentos > 0
        ? ((totalConversoes / totalAtendimentos) * 100).toFixed(1)
        : '0.0';

      var somaMinutos = 0;
      var countComTempo = 0;
      dadosFiltrados.forEach(function (d) {
        var min = calcularDuracaoMinutos(d.data_inicio, d.data_fim);
        if (min != null) {
          somaMinutos += min;
          countComTempo++;
        }
      });
      var tempoMedio = countComTempo > 0 ? somaMinutos / countComTempo : null;

      return {
        totalAtendimentos: totalAtendimentos,
        totalConversoes: totalConversoes,
        taxaConversao: taxaConversao,
        tempoMedio: tempoMedio,
      };
    },
    [dadosFiltrados]
  );

  useEffect(
    function () {
      setPaginaAtual(1);
      setFiltroNomeCorretor(null);
      setFiltroNomeAtendente(null);
    },
    [dados]
  );

  useEffect(
    function () {
      setPaginaAtual(1);
    },
    [filtroNomeCorretor, filtroNomeAtendente, filtroTipoAtendimento]
  );

  var mudarPagina = function (numeroPagina) {
    setPaginaAtual(numeroPagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  var buscarPorFamilia = async function (codigoFamilia) {
    setIsLoading(true);
    try {
      var filtros = {
        codigo_familia: codigoFamilia,
      };
      var response = await api.consultarAtendimentosLancamento(filtros);
      setDados(response || []);
      if (!response || response.length === 0) {
        toast.info('Nenhum resultado encontrado para este empreendimento.');
      }
    } catch (error) {
      var mensagem =
        error && error.message ? error.message : 'Erro ao buscar dados.';
      toast.error(mensagem);
    } finally {
      setIsLoading(false);
    }
  };

  var handleSelectorChange = function (selected) {
    setEmpreendimentoSelecionado(selected);
    setFiltroTipoAtendimento(OPTIONS_TIPO_ATENDIMENTO[0]);
    if (selected) {
      buscarPorFamilia(selected.value);
    } else {
      setDados([]);
    }
  };

  var ordenarDados = function (campo) {
    var novaDirecao = 'asc';
    if (ordenacao.campo === campo && ordenacao.direcao === 'asc') {
      novaDirecao = 'desc';
    }
    setOrdenacao({ campo: campo, direcao: novaDirecao });

    var dadosOrdenados = [].concat(dados).sort(function (a, b) {
      var valorA = a[campo];
      var valorB = b[campo];
      if (valorA == null) valorA = '';
      if (valorB == null) valorB = '';

      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return novaDirecao === 'asc' ? valorA - valorB : valorB - valorA;
      }
      var strA = String(valorA).toLowerCase();
      var strB = String(valorB).toLowerCase();
      if (novaDirecao === 'asc') {
        return strA.localeCompare(strB);
      }
      return strB.localeCompare(strA);
    });

    setDados(dadosOrdenados);
  };

  var getSortIcon = function (campo) {
    if (ordenacao.campo !== campo) return ' \u2195';
    return ordenacao.direcao === 'asc' ? ' \u2191' : ' \u2193';
  };

  var buscar = async function () {
    if (!empreendimentoSelecionado) {
      toast.warning('Selecione um empreendimento.');
      return;
    }

    var dataInicialConvertida = converterData(dataInicial);
    var dataFinalConvertida = converterData(dataFinal);

    if (dataInicialConvertida && dataFinalConvertida) {
      var dtInicio = moment(dataInicialConvertida);
      var dtFim = moment(dataFinalConvertida);
      if (dtInicio.isAfter(dtFim)) {
        toast.error('A "Data Inicial" deve ser menor que a "Data Final".');
        return;
      }
    }

    setIsLoading(true);
    try {
      var filtros = {
        codigo_familia: empreendimentoSelecionado.value,
        data_inicial: dataInicialConvertida,
        data_final: dataFinalConvertida,
        codigo_prospecto: prospecto ? parseInt(prospecto, 10) : null,
        codigo_pre_venda: preVenda ? parseInt(preVenda, 10) : null,
        codigo_proposta: proposta ? parseInt(proposta, 10) : null,
        quadra: quadra || null,
        lote: lote || null,
      };

      var response = await api.consultarAtendimentosLancamento(filtros);
      setDados(response || []);
      if (!response || response.length === 0) {
        toast.info('Nenhum resultado encontrado para os filtros informados.');
      }
    } catch (error) {
      var mensagem =
        error && error.message ? error.message : 'Erro ao buscar dados.';
      toast.error(mensagem);
    } finally {
      setIsLoading(false);
    }
  };

  var exportarExcel = async function () {
    if (!dadosFiltrados || dadosFiltrados.length === 0) {
      toast.error('Realize uma busca antes de exportar.');
      return;
    }

    setIsLoadingExcel(true);
    try {
      var dadosParaExcel = dadosFiltrados.map(function (item) {
        var dtInicio = item.data_inicio ? moment(item.data_inicio) : null;
        var dtFim = item.data_fim ? moment(item.data_fim) : null;
        return {
          nome_atendente: item.nome_atendente || '',
          data_inicio: dtInicio && dtInicio.isValid() ? dtInicio.format('DD/MM/YYYY HH:mm') : '',
          data_fim: dtFim && dtFim.isValid() ? dtFim.format('DD/MM/YYYY HH:mm') : '',
          nome_corretor: item.nome_corretor || '',
          codigo_prospecto: item.prospecto ? String(item.prospecto) : '',
          nome_cliente: item.nome_cliente || '',
          proposta: item.proposta ? String(item.proposta) : '',
          pre_venda: item.pre_venda ? String(item.pre_venda) : '',
          quadra: item.quadra || '',
          lote: item.lote || '',
          valor_vista: item.valor_vista
            ? formatarMonetario(item.valor_vista, false)
            : 'R$ 0,00',
          numero_mesa: item.numero_mesa || null,
        };
      });

      await salvarEmExcel(
        {
          titulo: empreendimentoSelecionado ? empreendimentoSelecionado.label : '',
          dados: dadosParaExcel,
        },
        '/exportador/excel/relatorio-atendimentos-lancamento'
      );
    } catch (error) {
      toast.error('Erro ao exportar para Excel.');
    } finally {
      setIsLoadingExcel(false);
    }
  };

  var renderizarPaginacao = function () {
    if (totalPaginas <= 1) return null;

    var itens = [];
    var maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={function () {
          mudarPagina(1);
        }}
        disabled={paginaAtual === 1}
      />
    );

    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={function () {
          mudarPagina(paginaAtual - 1);
        }}
        disabled={paginaAtual === 1}
      />
    );

    var inicioPaginas = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
    var fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);

    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (var numero = inicioPaginas; numero <= fimPaginas; numero++) {
      itens.push(
        <Pagination.Item
          key={numero}
          active={numero === paginaAtual}
          onClick={mudarPagina.bind(null, numero)}
        >
          {numero}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={function () {
          mudarPagina(paginaAtual + 1);
        }}
        disabled={paginaAtual === totalPaginas}
      />
    );

    itens.push(
      <Pagination.Last
        key="last"
        onClick={function () {
          mudarPagina(totalPaginas);
        }}
        disabled={paginaAtual === totalPaginas}
      />
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <div style={{ color: '#666', fontSize: '0.85rem' }}>
          Mostrando{' '}
          {dadosPaginados.length > 0
            ? (paginaAtual - 1) * ITENS_POR_PAGINA + 1
            : 0}{' '}
          at&eacute; {Math.min(paginaAtual * ITENS_POR_PAGINA, dadosFiltrados.length)} de{' '}
          {dadosFiltrados.length} registros
        </div>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul
        title="Relat&oacute;rio de Atendimento Lan&ccedil;amento"
        contemSelector={true}
        options={optionsEmpreendimento}
        selectorValue={empreendimentoSelecionado}
        onSelectorChange={handleSelectorChange}
        selectorPlaceholder={
          isLoadingEmpreendimentos
            ? 'Carregando...'
            : 'Selecione o empreendimento...'
        }
        selectorLoading={isLoadingEmpreendimentos}
        selectorIsClearable={true}
        selectorIsSearchable={true}
      />

      <div
        className="mb-3 p-3 mx-auto"
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
        }}
      >
        <Row className="align-items-end">
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Data Inicial
              </Form.Label>
              <div className="position-relative">
                <MaskedInput
                  mask={MASK_DATE}
                  className="form-control"
                  placeholder="dd/mm/aaaa"
                  value={dataInicial}
                  onChange={function (e) {
                    setDataInicial(e.target.value);
                  }}
                  style={{ paddingRight: '35px' }}
                />
                <div
                  className="position-absolute d-flex align-items-center"
                  style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  <FaCalendarAlt
                    className="text-secondary"
                    style={{ cursor: 'pointer' }}
                    onClick={function () {
                      setOpenDatePickerInicial(true);
                    }}
                  />
                </div>
                <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                  <DatePicker
                    open={openDatePickerInicial}
                    onOpen={function () {
                      setOpenDatePickerInicial(true);
                    }}
                    onClose={function () {
                      setOpenDatePickerInicial(false);
                    }}
                    value={dataInicial ? moment(dataInicial, 'DD/MM/YYYY') : null}
                    onChange={function (date) {
                      setDataInicial(date ? date.format('DD/MM/YYYY') : '');
                      setOpenDatePickerInicial(false);
                    }}
                    format="DD/MM/YYYY"
                    TextFieldComponent={function () {
                      return null;
                    }}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Data Final
              </Form.Label>
              <div className="position-relative">
                <MaskedInput
                  mask={MASK_DATE}
                  className="form-control"
                  placeholder="dd/mm/aaaa"
                  value={dataFinal}
                  onChange={function (e) {
                    setDataFinal(e.target.value);
                  }}
                  style={{ paddingRight: '35px' }}
                />
                <div
                  className="position-absolute d-flex align-items-center"
                  style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  <FaCalendarAlt
                    className="text-secondary"
                    style={{ cursor: 'pointer' }}
                    onClick={function () {
                      setOpenDatePickerFinal(true);
                    }}
                  />
                </div>
                <MuiPickersUtilsProvider utils={MomentUtils} locale="pt-br">
                  <DatePicker
                    open={openDatePickerFinal}
                    onOpen={function () {
                      setOpenDatePickerFinal(true);
                    }}
                    onClose={function () {
                      setOpenDatePickerFinal(false);
                    }}
                    value={dataFinal ? moment(dataFinal, 'DD/MM/YYYY') : null}
                    onChange={function (date) {
                      setDataFinal(date ? date.format('DD/MM/YYYY') : '');
                      setOpenDatePickerFinal(false);
                    }}
                    format="DD/MM/YYYY"
                    TextFieldComponent={function () {
                      return null;
                    }}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Prospecto
              </Form.Label>
              <Form.Control
                type="number"
                value={prospecto}
                onChange={function (e) {
                  setProspecto(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Pr&eacute;-Venda
              </Form.Label>
              <Form.Control
                type="number"
                value={preVenda}
                onChange={function (e) {
                  setPreVenda(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Proposta
              </Form.Label>
              <Form.Control
                type="number"
                value={proposta}
                onChange={function (e) {
                  setProposta(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={4} md={3} lg={2} className="mb-3">
            <Button
              onClick={buscar}
              disabled={isLoading || !empreendimentoSelecionado}
              style={{
                backgroundColor: '#0747a6',
                borderColor: '#0747a6',
                width: '100%',
              }}
            >
              {isLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaSearch /> Buscar
                </>
              )}
            </Button>
          </Col>
        </Row>
        <Row className="align-items-end">
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3 mb-lg-0">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Quadra
              </Form.Label>
              <Form.Control
                type="text"
                value={quadra}
                onChange={function (e) {
                  setQuadra(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3 mb-lg-0">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Lote
              </Form.Label>
              <Form.Control
                type="text"
                value={lote}
                onChange={function (e) {
                  setLote(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3 mb-lg-0">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Nome Corretor
              </Form.Label>
              <Select
                options={optionsNomeCorretor}
                value={filtroNomeCorretor}
                onChange={function (selected) {
                  setFiltroNomeCorretor(selected);
                }}
                components={{ DropdownIndicator }}
                styles={customStyles}
                isClearable={true}
                isSearchable={true}
                placeholder="Todos"
                noOptionsMessage={function () {
                  return 'Nenhum corretor encontrado';
                }}
                isDisabled={dados.length === 0}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3 mb-lg-0">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Nome Atendente
              </Form.Label>
              <Select
                options={optionsNomeAtendente}
                value={filtroNomeAtendente}
                onChange={function (selected) {
                  setFiltroNomeAtendente(selected);
                }}
                components={{ DropdownIndicator }}
                styles={customStyles}
                isClearable={true}
                isSearchable={true}
                placeholder="Todos"
                noOptionsMessage={function () {
                  return 'Nenhum atendente encontrado';
                }}
                isDisabled={dados.length === 0}
              />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={3} lg={2}>
            <Form.Group className="mb-3 mb-lg-0">
              <Form.Label style={{ fontSize: '0.8rem', marginBottom: '2px' }}>
                Tipo Atendimento
              </Form.Label>
              <Select
                options={OPTIONS_TIPO_ATENDIMENTO}
                value={filtroTipoAtendimento}
                onChange={function (selected) {
                  setFiltroTipoAtendimento(selected);
                }}
                components={{ DropdownIndicator }}
                styles={customStyles}
                isSearchable={false}
                isDisabled={dados.length === 0}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {dados.length > 0 && (
        <>
          {/* Tabela de resultados */}
          <div
            style={{
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              overflow: 'hidden',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.85rem',
                tableLayout: 'fixed',
              }}
            >
              <thead>
                <tr>
                  {COLUNAS.map(function (col) {
                    return (
                      <th
                        key={col.campo}
                        onClick={function () {
                          ordenarDados(col.campo);
                        }}
                        style={{
                          padding: '10px 8px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                          backgroundColor: '#0747a6',
                          color: '#fff',
                          borderBottom: '2px solid #0747a6',
                          textAlign: col.align,
                          fontWeight: 600,
                          width: col.width,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {col.titulo}
                        <span style={{ opacity: 0.7, marginLeft: '4px' }}>
                          {getSortIcon(col.campo)}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {dadosPaginados.map(function (item, index) {
                  return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: '#fff',
                        borderBottom: '1px solid #eee',
                      }}
                    >
                      {COLUNAS.map(function (col) {
                        var valor = renderizarCelula(item, col);
                        var cellStyle = {
                          padding: '8px',
                          textAlign: col.align,
                        };
                        if (col.truncate) {
                          cellStyle.whiteSpace = 'nowrap';
                          cellStyle.overflow = 'hidden';
                          cellStyle.textOverflow = 'ellipsis';
                        }
                        return (
                          <td
                            key={col.campo}
                            style={cellStyle}
                            title={col.truncate ? String(valor) : undefined}
                          >
                            {valor}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderizarPaginacao()}

          <Row className="mb-3">
            <Col className="text-right">
              <Button
                onClick={exportarExcel}
                disabled={isLoadingExcel}
                style={{
                  backgroundColor: '#7bb32e',
                  borderColor: '#7bb32e',
                }}
              >
                {isLoadingExcel ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaFileExcel style={{ marginRight: '6px' }} />
                    Salvar em Excel
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Cards de resumo */}
          <Row className="mb-3">
            <Col xs={6} lg={3} className="mb-3 mb-lg-0">
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                    Total de Atendimentos
                  </Card.Title>
                  <h4 className="mb-0 text-primary">{resumo.totalAtendimentos}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} lg={3} className="mb-3 mb-lg-0">
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                    Total de Conversões (Vendas)
                  </Card.Title>
                  <h4 className="mb-0 text-primary">{resumo.totalConversoes}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} lg={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                    Taxa de Conversão
                  </Card.Title>
                  <h4 className="mb-0 text-primary">{resumo.taxaConversao}%</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} lg={3}>
              <Card className="text-center shadow-sm">
                <Card.Body>
                  <Card.Title className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                    Tempo Médio de Atendimento
                  </Card.Title>
                  <h4 className="mb-0 text-primary">{formatarTempo(resumo.tempoMedio)}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Gráfico tempo médio por colaborador */}
          <GraficoTempoMedioColaborador dados={dadosFiltrados} />
        </>
      )}

      {dados.length === 0 && !isLoading && empreendimentoSelecionado && (
        <div
          className="text-center py-4"
          style={{ color: '#888', fontSize: '0.9rem' }}
        >
          Nenhum resultado encontrado. Utilize os filtros e clique em Buscar.
        </div>
      )}
    </Container>
  );
};

export default RelatorioAtendimentoLancamento;
